import type { UATHub } from '../../../services/fixedRouteUatClient';
import type { Environment } from './EnvironmentSelector';

interface CacheEntry {
  timestamp: number;
  data: UATHub[];
}

const HUBS_CACHE_KEY = 'atlas_hubsCache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes

// In-memory cache for the current session
let sessionCache: Map<string, CacheEntry> = new Map();

// Initialize cache from sessionStorage on first load
if (typeof window !== 'undefined') {
  const savedCache = sessionStorage.getItem(HUBS_CACHE_KEY);
  if (savedCache) {
    try {
      const parsedCache = JSON.parse(savedCache);
      // Reconstruct Map from plain object
      sessionCache = new Map(Object.entries(parsedCache).map(([key, value]) => [key, value as CacheEntry]));
      console.log('[hubsCache] Loaded cache from sessionStorage:', sessionCache.size, 'entries');
    } catch (e) {
      console.error('[hubsCache] Failed to parse cache from sessionStorage', e);
      sessionStorage.removeItem(HUBS_CACHE_KEY);
    }
  }
}

function saveCacheToSessionStorage() {
  if (typeof window !== 'undefined') {
    // Convert Map to a plain object for sessionStorage
    const plainObjectCache = Object.fromEntries(sessionCache);
    sessionStorage.setItem(HUBS_CACHE_KEY, JSON.stringify(plainObjectCache));
  }
}

/**
 * Generates a unique cache key for hubs list.
 */
function generateCacheKey(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string
): string {
  return `${environment}-${productId}-${startDate}-${endDate}-hubs`;
}

/**
 * Retrieves cached hubs list if available and not expired.
 */
export function getCachedHubsList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string
): UATHub[] | null {
  const key = generateCacheKey(environment, productId, startDate, endDate);
  const entry = sessionCache.get(key);

  if (entry && (Date.now() - entry.timestamp < CACHE_EXPIRATION_MS)) {
    console.log(`[hubsCache] Cache hit for key: ${key}`);
    return entry.data;
  }

  if (entry) {
    console.log(`[hubsCache] Cache expired for key: ${key}`);
    sessionCache.delete(key); // Remove expired entry
    saveCacheToSessionStorage();
  }

  return null;
}

/**
 * Stores hubs list in the cache.
 */
export function setCachedHubsList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string,
  data: UATHub[]
): void {
  const key = generateCacheKey(environment, productId, startDate, endDate);
  sessionCache.set(key, { timestamp: Date.now(), data });
  console.log(`[hubsCache] Cache set for key: ${key}`);
  saveCacheToSessionStorage();
}

/**
 * Clears a specific entry from the cache.
 */
export function clearCachedHubsList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string
): void {
  const key = generateCacheKey(environment, productId, startDate, endDate);
  if (sessionCache.delete(key)) {
    console.log(`[hubsCache] Cache cleared for key: ${key}`);
    saveCacheToSessionStorage();
  }
}

/**
 * Clears all entries from the cache.
 */
export function clearAllHubsCache(): void {
  sessionCache.clear();
  console.log('[hubsCache] All cache cleared.');
  saveCacheToSessionStorage();
}

